import { ValueLabelOption } from '@elementary/common-ui/types';
import { ColumnResource, Resource } from 'shared/types';
import { YamlFormResourceOptions } from './types';

type Props = {
  resourceIds: string[];
  appliesTo: 'column' | 'table';
  columnsResourcesData: ColumnResource[];
  tableResourcesData: Resource[];
};

const uniqueSorted = (values: string[]): string[] =>
  [...new Set(values)].sort((a, b) => a.localeCompare(b));

const getSelectedTableIds = ({
  resourceIds,
  appliesTo,
  columnsResourcesData,
}: Pick<
  Props,
  'resourceIds' | 'appliesTo' | 'columnsResourcesData'
>): string[] => {
  if (appliesTo === 'table') {
    return uniqueSorted(resourceIds);
  }
  return uniqueSorted(
    columnsResourcesData
      .filter((column) => resourceIds.includes(column.id))
      .map((column) => column.model_id),
  );
};

const intersectColumnNames = (
  columnsResourcesData: ColumnResource[],
  tableIds: string[],
  matchesColumn: (column: ColumnResource) => boolean,
): ValueLabelOption[] => {
  if (tableIds.length === 0) {
    return [];
  }
  const namesByTable = tableIds.map(
    (tableId) =>
      new Set(
        columnsResourcesData
          .filter(
            (column) => column.model_id === tableId && matchesColumn(column),
          )
          .map((column) => column.name),
      ),
  );
  const [first, ...rest] = namesByTable;
  const sharedNames = rest.reduce(
    (intersection, names) =>
      new Set([...intersection].filter((name) => names.has(name))),
    first,
  );
  return uniqueSorted([...sharedNames]).map((name) => ({
    label: name,
    value: name,
  }));
};

export const toDbtRelation = (table: Resource): string =>
  table.source_name
    ? `source('${table.source_name}', '${table.name}')`
    : `ref('${table.name}')`;

const toTableOption = (table: Resource): ValueLabelOption => ({
  label: table.source_name ? `${table.source_name}.${table.name}` : table.name,
  value: toDbtRelation(table),
});

export const getYamlFormResourceOptions = ({
  resourceIds,
  appliesTo,
  columnsResourcesData,
  tableResourcesData,
}: Props): YamlFormResourceOptions => {
  const selectedTableIds = getSelectedTableIds({
    resourceIds,
    appliesTo,
    columnsResourcesData,
  });
  const selectedTableIdSet = new Set(selectedTableIds);

  return {
    columns: intersectColumnNames(
      columnsResourcesData,
      selectedTableIds,
      () => true,
    ),
    timestampColumns: intersectColumnNames(
      columnsResourcesData,
      selectedTableIds,
      (column) => column.type === 'timestamp',
    ),
    tables: tableResourcesData
      .filter((table) => !selectedTableIdSet.has(table.id))
      .map(toTableOption),
  };
};
